import "survey-core/defaultV2.min.css";
import config from "./config.json";
import React, { useContext, useEffect, useState } from "react";
import { Stack, Typography, Paper, Divider, Box, Tabs, Tab, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, ToggleButton, ToggleButtonGroup, FormControl, InputLabel, Select, MenuItem, Tooltip as MuiTooltip, Grid, Checkbox, ListItemText, Chip, Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios"; // For making API calls
import { useAuth } from "react-oidc-context"; 
import { useQuery } from "@tanstack/react-query";
import { Bar, Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LogarithmicScale, // Import LogarithmicScale
  PointElement, // Added for line chart points
  LineElement,  // Added for line chart lines
  LineController, // Added for mixed bar+line charts
  ScatterController,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js';

import { ResponsiveTreeMap, TreeMapCommonProps } from '@nivo/treemap'; // Import Nivo Treemap
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import { useTheme } from "@mui/material/styles";
import AppContext from "./AppContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale, // Register LogarithmicScale
  BarElement,
  PointElement, // Registered for line chart points
  LineElement,  // Registered for line chart lines
  LineController, // Registered for mixed bar+line charts
  ScatterController,
  Title,
  Tooltip,
  Legend
);

// Helper function to display rating as stars
const renderRatingStars = (rating: number | string | undefined): string => {
  if (rating === undefined || rating === null) {
    return 'N/A';
  }
  const numRating = typeof rating === 'string' ? parseFloat(rating) : rating;
  if (isNaN(numRating) || numRating < 0) {
    return 'N/A';
  }

  const fullStars = Math.floor(numRating);
  const halfStar = numRating - fullStars >= 0.5 ? '½' : ''; // Using a half-star character
  const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

  return '★'.repeat(fullStars) + halfStar + '☆'.repeat(emptyStars);
};

// Base64 string for a 1x1 transparent PNG, used to identify non-displayable images
const EMPTY_IMAGE_BASE64 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAwAB/ulB22cAAAAASUVORK5CYII=";

// Helper function to compare semantic versions (e.g., "1.0", "1.10", "1.2")
const compareVersions = (a: string, b: string): number => {
  const aParts = a.split('.').map(Number);
  const bParts = b.split('.').map(Number);
  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i++) {
    const aPart = aParts[i] || 0; // Default to 0 if a part is missing (e.g., "1.0" vs "1.0.1")
    const bPart = bParts[i] || 0;
    if (aPart < bPart) return -1;
    if (aPart > bPart) return 1;
  }
  return 0;
};

const GradientLegend = ({ min, max }: { min: number; max: number }) => {
  const legendTheme = useTheme();
  const legendIsDark = legendTheme.palette.mode === "dark";

  // Don't show a legend if all values are the same or no data
  if (max === 0 || min === max) {
    return null;
  }

  // The same HSL values from getColorForVotes
  const gradient = 'linear-gradient(to right, hsla(210, 70%, 90%, 0.7), hsla(210, 70%, 40%, 0.7))';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 1, pr: 2 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{min}</Typography>
      <Box
        sx={{
          width: 120,
          height: 12,
          background: gradient,
          borderRadius: '6px',
          border: `1px solid ${legendIsDark ? '#555' : '#ddd'}`
        }}
      />
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{max}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', ml: -0.5 }}>Votes</Typography>
    </Box>
  );
};

// Helper: truncate text and strip HTML tags for preview
const truncateText = (html: string | undefined, maxLen: number = 80): string => {
  if (!html) return '';
  const text = html.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, ' ');
  return text.length > maxLen ? text.substring(0, maxLen) + '...' : text;
};

// Detail row component for feedback/log items shown in a dialog
const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </Typography>
    <Box sx={{ mt: 0.5 }}>{children}</Box>
  </Box>
);

interface FeedbackItem {
  rating: number | string;
  question: string;
  answer: string;
  feedback: string;
  timestamp: string;
  user_email?: string;
  username?: string;
  user_ip?: string;
  question_category?: string; 
  question_category_explanation?: string; // Added for explanation
  app_version?: string; // Added for app version
  user_group?: string;
  is_question_a_follow_up_question?: number;
  is_fixed?: number;
  fix_details?: string;
}

interface LogItem {
  question: string;
  question_category?: string; // Made optional as it might be 'none' or missing
  question_category_explanation?: string; // Added for explanation in category chart tooltip
  answer: string;
  answer_image?: string; // Optional, as it might not always be present
  score_threshold: number;
  use_only_transcripts_flag: boolean;
  history_retention: number;
  timestamp: string;
  user_ip?: string;
  user_agent?: string; // Can be very long, consider how to display
  user_email?: string;
  username?: string;
  user_group?: string; 
  is_question_a_follow_up_question?: number;
}

interface RawCostDataItem {
  date: string;
  service: string;
  cost: number;
  aggregation: 'month' | 'day';
  avg_cost_per_request?: number;
  total_requests?: number;
}
interface CostDataRow {
  date: string;
  [service: string]: number | string; // Service columns will have costs (number) or avg_cost (number) or date (string)
  total_cost: number;
  total_requests: number;
}

interface TreemapNode {
  name: string;
  loc: number; // 'loc' for lines of code, or in our case, number of queries
}

function Admin() {
  const { user } = useContext<any>(AppContext);
  const auth = useAuth(); // Get auth context if your token comes from here
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [activeTab, setActiveTab] = useState(0);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  // State for User Feedbacks Tab
  const [selectedAppVersion, setSelectedAppVersion] = useState<string>("all");
  const [chartViewMode, setChartViewMode] = useState<'stars' | 'categories'>('stars');
  const [selectedQuestionCategory, setSelectedQuestionCategory] = useState<string>("all");
  const [selectedUsernameFeedback, setSelectedUsernameFeedback] = useState<string[]>([]);
  const [barStackMode, setBarStackMode] = useState<'stacked' | 'grouped'>('stacked');
  const [categoryChartViewMode, setCategoryChartViewMode] = useState<'discrete' | 'continuous'>('discrete');
  const [minFeedbackVotes, setMinFeedbackVotes] = useState(0);
  const [maxFeedbackVotes, setMaxFeedbackVotes] = useState(0);
  const [chartFilterCategory, setChartFilterCategory] = useState<string | null>(null);
  const [chartFilterStar, setChartFilterStar] = useState<number | null>(null);
  const [ratingChartData, setRatingChartData] = useState<ChartData<'bar'>>({ datasets: [] });
  const [feedbackCategoryExplanations, setFeedbackCategoryExplanations] = useState<{ [key: string]: string }>({});

  // State for detail dialogs
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [selectedLog, setSelectedLog] = useState<LogItem | null>(null);

  // State for Logs Tab
  const [selectedUsernameLog, setSelectedUsernameLog] = useState<string[]>([]);
  const [logQueriesChartData, setLogQueriesChartData] = useState<ChartData<'bar'>>({ datasets: [] });
  const [questionCategoryChartData, setQuestionCategoryChartData] = useState<ChartData<'bar'>>({ datasets: [] });
  const [userQueriesTreemapData, setUserQueriesTreemapData] = useState<{ name: string; children: TreemapNode[] } | null>(null);
  const [questionCategoryExplanations, setQuestionCategoryExplanations] = useState<{ [key: string]: string }>({});
  const [logFilterDate, setLogFilterDate] = useState<string | null>(null);
  const [logFilterCategory, setLogFilterCategory] = useState<string | null>(null);
  const [logFilterUser, setLogFilterUser] = useState<string | null>(null);
  const [top10LogUsers, setTop10LogUsers] = useState<string[]>([]);

  // State for AWS Costs Tab
  const [awsCosts, setAwsCosts] = useState<CostDataRow[]>([]);
  const [serviceColumns, setServiceColumns] = useState<string[]>([]);
  const [maxOverallCost, setMaxOverallCost] = useState(0);
  const [maxOverallAvgCost, setMaxOverallAvgCost] = useState(0);
  const [logScaleRequestsScatter, setLogScaleRequestsScatter] = useState(false);
  const [awsChartData, setAwsChartData] = useState<ChartData<'bar' | 'line'>>({ datasets: [] });
  const [awsScatterChartData, setAwsScatterChartData] = useState<ChartData<'scatter'>>({ datasets: [] });
  const [selectedAggregation, setSelectedAggregation] = useState<'month' | 'day'>('month');
  const [costViewMode, setCostViewMode] = useState<'cost' | 'avg_cost_per_request'>('cost');

  const [allQuestionCategories, setAllQuestionCategories] = useState<string[]>([]); // For the filter dropdown
  const [allAppVersions, setAllAppVersions] = useState<string[]>([]);
  const [allUsernamesFeedback, setAllUsernamesFeedback] = useState<string[]>([]);
  const [allUsernamesLog, setAllUsernamesLog] = useState<string[]>([]);
  
  // Set Chart.js global defaults synchronously so remounted charts (via key change) pick up correct colors immediately
  const textColor = isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.67)';
  const gridColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  ChartJS.defaults.color = textColor;
  ChartJS.defaults.borderColor = gridColor;

  const userApiKey = auth.user?.profile?.['custom:user_api_key'] as string;

  const fetchUserFeedbacks = async (): Promise<FeedbackItem[]> => {
    const feedbackUrl = `${config.api.chatbot.url}/log-vote`;
    const response = await axios.get<{ feedback_entries: FeedbackItem[] }>(feedbackUrl, {
      headers: { 'Authorization': auth.user!.id_token, 'X-Api-Key': userApiKey },
      timeout: config.timeouts.chatApi,
    });
    return response.data.feedback_entries || [];
  };

  const { data: fetchedFeedbacks, isLoading: isLoadingFeedbacks, error: feedbackFetchError } = useQuery({
    queryKey: ['feedbacks', userApiKey],
    queryFn: fetchUserFeedbacks,
    enabled: activeTab === 0 && !!auth.user?.id_token && !!userApiKey,
  });

  const fetchUserLogs = async (): Promise<LogItem[]> => {
    const logsUrl = `${config.api.chatbot.url}/log`;
    const response = await axios.get<{ log_entries: LogItem[] }>(logsUrl, {
      headers: { 'Authorization': auth.user!.id_token, 'X-Api-Key': userApiKey },
      timeout: config.timeouts.chatApi,
    });
    return response.data.log_entries || [];
  };

  const { data: fetchedLogs, isLoading: isLoadingLogs, error: logFetchError } = useQuery({
    queryKey: ['logs', userApiKey],
    queryFn: fetchUserLogs,
    enabled: activeTab === 1 && !!auth.user?.id_token && !!userApiKey,
  });

  const fetchAwsCosts = async (): Promise<RawCostDataItem[]> => {
    const costsUrl = `${config.api.chatbot.url}/aws-costs`;
    const response = await axios.get<{ costs: RawCostDataItem[] }>(costsUrl, {
      headers: { 'Authorization': auth.user!.id_token, 'X-Api-Key': userApiKey },
      timeout: config.timeouts.chatApi,
    });
    return response.data.costs || [];
  };
  
  const { data: allFetchedCosts, isLoading: isLoadingCosts, error: costFetchError } = useQuery({
    queryKey: ['awsCosts', userApiKey],
    queryFn: fetchAwsCosts,
    enabled: activeTab === 2 && !!auth.user?.id_token && !!userApiKey,
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
  };

  const handleAggregationChange = (
    event: React.MouseEvent<HTMLElement>,
    newAggregation: 'month' | 'day' | null,
  ) => {
    if (newAggregation !== null) {
      setSelectedAggregation(newAggregation);
    }
  };

  // Helper function to determine cell color for the heatmap based on the current view mode
  const getHeatmapCellStyle = (value: number): React.CSSProperties => {
    const maxValue = costViewMode === 'cost' ? maxOverallCost : maxOverallAvgCost;
    if (value <= 0 || maxValue === 0) {
      return { backgroundColor: isDark ? '#2a2a2a' : '#f5f5f5', color: isDark ? 'rgba(255,255,255,0.87)' : 'rgba(0, 0, 0, 0.87)' };
    }

    const intensity = Math.min(value / maxValue, 1);
    let backgroundColor: string;
    let textColor: string;

    if (isDark) {
      const lightness = 20 + intensity * 30; // 20% to 50% in dark mode
      backgroundColor = `hsl(210, 40%, ${lightness}%)`;
      textColor = lightness > 35 ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)';
    } else {
      const lightness = 90 - intensity * 50;
      backgroundColor = `hsl(0, 0%, ${lightness}%)`;
      textColor = lightness < 65 ? 'white' : 'rgba(0, 0, 0, 0.87)';
    }
    return { backgroundColor, color: textColor };
  };

  // useEffect to process data when allFetchedCosts or selectedAggregation changes
  useEffect(() => {
    if (!allFetchedCosts) {
      setAwsCosts([]);
      setServiceColumns([]);
      return;
    }

    const initialFilteredCosts = allFetchedCosts.filter(item => item.aggregation === selectedAggregation);
    const uniqueServices = Array.from(new Set(initialFilteredCosts.map(item => item.service)));

    // Set the selected services on initial data load, excluding VPC by default
    if (selectedServices.length === 0 && uniqueServices.length > 0) {
        const defaultServices = uniqueServices.filter(service => service !== "VPC"); // excluding VPC (fixed costs) by default.
        setSelectedServices(defaultServices);
        return; // Return here to let the effect re-run with the new state
    }

    // Now, filter the raw costs based on the user-selected services
    const filteredRawCosts = initialFilteredCosts.filter(item => selectedServices.includes(item.service));

    if (filteredRawCosts.length === 0) {
      setAwsCosts([]);
      setServiceColumns(uniqueServices);
      return;
    }

    const serviceCostTotals: { [service: string]: number } = {};
    initialFilteredCosts.forEach(item => { // Use initial list to sort all services correctly
      serviceCostTotals[item.service] = (serviceCostTotals[item.service] || 0) + item.cost;
    });

    const sortedUniqueServices = uniqueServices.sort((a, b) => {
        return (serviceCostTotals[b] || 0) - (serviceCostTotals[a] || 0);
    });
    setServiceColumns(sortedUniqueServices);

    const costsByDate: { [date: string]: CostDataRow } = {};
    // We iterate over the service-filtered data to build the table rows
    filteredRawCosts.forEach(item => {
      if (!costsByDate[item.date]) {
        costsByDate[item.date] = {
          date: item.date,
          total_cost: 0,
          total_requests: 0,
        };
        // Initialize all possible services to ensure consistent object shape
        sortedUniqueServices.forEach(service => {
          costsByDate[item.date][service] = 0;
        });
      }
      const valueForView = costViewMode === 'cost' ? item.cost : (item.avg_cost_per_request || 0);
      costsByDate[item.date][item.service] = valueForView;
    });

    // To calculate the correct total cost and requests for the period (unaffected by service filter),
    // we need to iterate over the data that is only filtered by date aggregation.
    const totalsByDate: { [date: string]: { total_cost: number; total_requests: number } } = {};
    filteredRawCosts.forEach(item => {
        if (!totalsByDate[item.date]) {
            totalsByDate[item.date] = { total_cost: 0, total_requests: 0 };
        }
        totalsByDate[item.date].total_cost += item.cost;
        // Total requests are assumed to be a daily value duplicated across services, so Math.max prevents over-summing.
        totalsByDate[item.date].total_requests = Math.max(totalsByDate[item.date].total_requests, item.total_requests || 0);
    });

    // Combine the filtered data with the correct totals
    Object.keys(costsByDate).forEach(date => {
        if (totalsByDate[date]) {
            costsByDate[date].total_cost = totalsByDate[date].total_cost;
            costsByDate[date].total_requests = totalsByDate[date].total_requests;
        }
    });

    const transformedData = Object.values(costsByDate).sort((a, b) => b.date.localeCompare(a.date));
    setAwsCosts(transformedData);

    let currentMaxCost = 0;
    let currentMaxAvgCost = 0;
    filteredRawCosts.forEach(item => {
        if (item.cost > currentMaxCost) currentMaxCost = item.cost;
        if ((item.avg_cost_per_request || 0) > currentMaxAvgCost) currentMaxAvgCost = item.avg_cost_per_request || 0;
    });
    setMaxOverallCost(currentMaxCost);
    setMaxOverallAvgCost(currentMaxAvgCost);

    const sortedCostsForChart = [...transformedData].sort((a, b) => a.date.localeCompare(b.date));
    const chartLabels: string[] = sortedCostsForChart.map(row => row.date);

    const serviceColors = [
      'rgba(255, 99, 132, 0.7)', 'rgba(54, 162, 235, 0.7)', 'rgba(255, 206, 86, 0.7)',
      'rgba(75, 192, 192, 0.7)', 'rgba(153, 102, 255, 0.7)', 'rgba(255, 159, 64, 0.7)',
      'rgba(199, 199, 199, 0.7)', 'rgba(83, 102, 255, 0.7)', 'rgba(40, 159, 64, 0.7)',
      'rgba(210, 99, 132, 0.7)', 'rgba(64, 159, 255, 0.7)', 'rgba(255, 102, 86, 0.7)'
    ];

    // Filter the sortedUniqueServices to only include those selected for the chart
    const barChartDatasets = sortedUniqueServices
      .filter(service => selectedServices.includes(service))
      .map((service, index) => ({
      type: 'bar' as const,
      label: service,
      data: sortedCostsForChart.map(row => (typeof row[service] === 'number' ? row[service] as number : 0)),
      backgroundColor: serviceColors[index % serviceColors.length],
      yAxisID: 'y-cost',
    }));

    const lineChartDataset = {
        type: 'line' as const,
        label: 'Total Requests',
        data: sortedCostsForChart.map(row => row.total_requests),
        borderColor: 'rgb(108, 117, 125)',
        backgroundColor: 'rgba(108, 117, 125, 0.3)',
        borderWidth: 2,
        borderDash: [3, 3],
        tension: 0.3,
        yAxisID: 'y-requests',
    };

    setAwsChartData({ labels: chartLabels, datasets: [...barChartDatasets, lineChartDataset] });

    if (selectedAggregation === 'day') {
      const dailyCostsByService: { [date: string]: { [service: string]: number } } = {};
      filteredRawCosts.forEach(item => {
          if (!dailyCostsByService[item.date]) {
              dailyCostsByService[item.date] = {};
          }
          dailyCostsByService[item.date][item.service] = item.cost;
      });

      const scatterDataPoints = transformedData.map(row => {
        const serviceCostsForDay = dailyCostsByService[row.date] || {};
        let dominantService = '';
        let maxCost = -1;

        Object.entries(serviceCostsForDay).forEach(([service, cost]) => {
            if (cost > maxCost) {
                maxCost = cost;
                dominantService = service;
            }
        });

        const avgCost = row.total_requests > 0 ? row.total_cost / row.total_requests : 0;

        return {
            x: row.total_requests,
            totalCost: row.total_cost,
            dominantService: dominantService,
            avgCost: avgCost,
            date: row.date,
        };
      });

      const avgCosts = scatterDataPoints.map(p => p.avgCost).filter(c => c > 0);
      const minAvgCost = avgCosts.length > 0 ? Math.min(...avgCosts) : 0;
      const maxAvgCost = avgCosts.length > 0 ? Math.max(...avgCosts) : 0;

      const totalCosts = scatterDataPoints.map(p => p.totalCost).filter(c => c > 0);
      const minTotalCost = totalCosts.length > 0 ? Math.min(...totalCosts) : 0;
      const maxTotalCost = totalCosts.length > 0 ? Math.max(...totalCosts) : 0;

      const scatterDatasets = sortedUniqueServices
        .filter(service => selectedServices.includes(service))
        .map((service, index) => ({
        label: service,
        data: scatterDataPoints
          .filter(p => p.dominantService === service)
          .map(p => ({
            x: p.x,
            y: costViewMode === 'cost' ? p.totalCost : p.avgCost,
            date: p.date,
            avgCost: p.avgCost,
            totalCost: p.totalCost,
            requests: p.x,
           })),
        backgroundColor: serviceColors[index % serviceColors.length],
        radius: (context: any) => {
          if (context.type !== 'data') return 3;
          if (costViewMode === 'cost') {
            if (!context.raw.avgCost) return 3;
            if (maxAvgCost === minAvgCost) return 8;
            const normalized = (context.raw.avgCost - minAvgCost) / (maxAvgCost - minAvgCost);
            return 3 + normalized * 12;
          } else {
            if (!context.raw.totalCost) return 3;
            if (maxTotalCost === minTotalCost) return 8;
            const normalized = (context.raw.totalCost - minTotalCost) / (maxTotalCost - minTotalCost);
            return 3 + normalized * 12;
          }
        },
      }));
      setAwsScatterChartData({ datasets: scatterDatasets });
    } else {
      setAwsScatterChartData({ datasets: [] });
    }
  }, [allFetchedCosts, selectedAggregation, costFetchError, costViewMode, logScaleRequestsScatter, selectedServices]);

  // useEffect to fetch user feedbacks
  useEffect(() => {
    if (!fetchedFeedbacks) return;

    const versions = Array.from(new Set(fetchedFeedbacks.map(fb => fb.app_version).filter(Boolean) as string[])).sort(compareVersions).reverse();
    setAllAppVersions(versions);
    if (versions.length > 0 && selectedAppVersion === "all") {
      setSelectedAppVersion(versions[0]);
    }

    const categories = Array.from(new Set(fetchedFeedbacks.map(fb => fb.question_category || "Uncategorized"))).sort();
    setAllQuestionCategories(categories);

    const usernames = Array.from(new Set(fetchedFeedbacks.map(fb => fb.username || fb.user_email).filter((id): id is string => !!id))).sort();
    setAllUsernamesFeedback(usernames);
  }, [fetchedFeedbacks]);

  useEffect(() => {
    if (!fetchedLogs) return;

    const logUsernames = Array.from(new Set(fetchedLogs.map(log => log.username || log.user_email).filter((id): id is string => !!id))).sort();
    setAllUsernamesLog(logUsernames);
  }, [fetchedLogs]);

  // useEffect to prepare data for the log queries per day chart
  useEffect(() => {
    if (!fetchedLogs) return;
    if (activeTab === 1 && fetchedLogs.length > 0 ) {
      const logsForChart = selectedUsernameLog.length > 0
        ? fetchedLogs.filter(log => selectedUsernameLog.includes(log.username || log.user_email || ''))
        : fetchedLogs;

      if (logsForChart.length === 0) { // If filtering results in no data
        setLogQueriesChartData({ labels: [], datasets: [] });
        return;
      }
      const queriesByDate: { [date: string]: number } = {};
      logsForChart.forEach(log => {
        const date = new Date(log.timestamp).toLocaleDateString('en-CA'); // YYYY-MM-DD format for sorting
        queriesByDate[date] = (queriesByDate[date] || 0) + 1;
      });

      const sortedDates = Object.keys(queriesByDate).sort((a,b) => new Date(a).getTime() - new Date(b).getTime());

      const chartLabels = sortedDates.map(date => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })); // User-friendly date
      const chartDataPoints = sortedDates.map(date => queriesByDate[date]);

      const backgroundColors = sortedDates.map(date => {
        if (logFilterDate && date === logFilterDate) {
            return 'hsla(10, 80%, 60%, 0.8)'; // Highlight color
        }
        return 'rgba(75, 192, 192, 0.7)';
      });
      const borderColors = sortedDates.map(date => {
          if (logFilterDate && date === logFilterDate) {
              return 'hsla(10, 80%, 60%, 1)'; // Highlight border
          }
          return 'rgba(75, 192, 192, 1)';
      });

      setLogQueriesChartData({
        labels: chartLabels,
        datasets: [
          {
            label: 'Queries per Day',
            data: chartDataPoints,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 1,
            originalDates: sortedDates,
          },
        ] as any, // Use 'as any' to allow custom property
      });
    } else if (activeTab === 1) { // Clear chart if no logs or tab not active
      setLogQueriesChartData({ labels: [], datasets: [] });
    }

    // --- Start of User Queries Treemap Data ---
    const logsForTreemap = selectedUsernameLog.length > 0
      ? fetchedLogs.filter(log => selectedUsernameLog.includes(log.username || log.user_email || ''))
      : fetchedLogs;

    const queriesByUser: { [user: string]: number } = {};
    logsForTreemap.forEach(log => {
      const userIdentifier = log.username || log.user_email || "Unknown User";
      queriesByUser[userIdentifier] = (queriesByUser[userIdentifier] || 0) + 1;
    });

    const sortedUsers = Object.entries(queriesByUser)
      .sort(([, aCount], [, bCount]) => bCount - aCount);

    const top10Users = sortedUsers.slice(0, 10);
    const restUsersCount = sortedUsers.slice(10).reduce((sum, [, count]) => sum + count, 0);
    const top10UserNames = top10Users.map(([name]) => name);
    setTop10LogUsers(top10UserNames);

    const treemapChildren: TreemapNode[] = top10Users.map(([name, loc]) => ({ name, loc }));
    if (restUsersCount > 0 && sortedUsers.length > 10) {
      treemapChildren.push({ name: "Other Users", loc: restUsersCount });
    }

    if (treemapChildren.length > 0) {
      setUserQueriesTreemapData({
        name: "Total Queries by User",
        children: treemapChildren
      });
    } else {
      setUserQueriesTreemapData(null);
    }
    // --- End of User Queries Treemap Data ---
  }, [fetchedLogs, activeTab, selectedUsernameLog, logFilterDate]);
  
  // useEffect to prepare data for the log queries per day chart & user treemap
  useEffect(() => {
    if (!fetchedLogs) return;
    if (activeTab === 1 && fetchedLogs.length > 0 ) {
      // Base filter for dropdown
      const baseLogs = selectedUsernameLog.length > 0
        ? fetchedLogs.filter(log => selectedUsernameLog.includes(log.username || log.user_email || ''))
        : fetchedLogs;

      // --- Data for User Queries Treemap (filtered by date and category) ---
      // This is calculated first to determine the top 10 users, which is needed for other chart filters.
      const logsForTreemap = baseLogs.filter(log => {
        const dateFilterMatch = !logFilterDate || new Date(log.timestamp).toLocaleDateString('en-CA') === logFilterDate;
        const categoryFilterMatch = !logFilterCategory || (log.question_category || 'Uncategorized') === logFilterCategory;
        return dateFilterMatch && categoryFilterMatch;
      });

      const queriesByUser: { [user: string]: number } = {};
      logsForTreemap.forEach(log => {
        const userIdentifier = log.username || log.user_email || "Unknown User";
        queriesByUser[userIdentifier] = (queriesByUser[userIdentifier] || 0) + 1;
      });

      const sortedUsers = Object.entries(queriesByUser).sort(([, aCount], [, bCount]) => bCount - aCount);
      const top10Users = sortedUsers.slice(0, 10);
      const restUsersCount = sortedUsers.slice(10).reduce((sum, [, count]) => sum + count, 0);
      const currentTop10UserNames = top10Users.map(([name]) => name);
      setTop10LogUsers(currentTop10UserNames); // Set state for other hooks/components

      const treemapChildren: TreemapNode[] = top10Users.map(([name, loc]) => ({ name, loc }));
      if (restUsersCount > 0 && sortedUsers.length > 10) {
        treemapChildren.push({ name: "Other Users", loc: restUsersCount });
      }

      if (treemapChildren.length > 0) {
        setUserQueriesTreemapData({ name: "Total Queries by User", children: treemapChildren });
      } else {
        setUserQueriesTreemapData(null);
      }

      // --- Data for Queries per Day Chart (filtered by category and user) ---
      const logsForDateChart = baseLogs.filter(log => {
        const categoryFilterMatch = !logFilterCategory || (log.question_category || 'Uncategorized') === logFilterCategory;
        const userIdentifier = log.username || log.user_email || '';
        const userFilterMatch = !logFilterUser || 
            (logFilterUser === 'Other Users' 
                ? userIdentifier && !currentTop10UserNames.includes(userIdentifier)
                : userIdentifier === logFilterUser);
        return categoryFilterMatch && userFilterMatch;
      });

      if (logsForDateChart.length === 0) {
        setLogQueriesChartData({ labels: [], datasets: [] });
      } else {
        const queriesByDate: { [date: string]: number } = {};
        logsForDateChart.forEach(log => {
          const date = new Date(log.timestamp).toLocaleDateString('en-CA');
          queriesByDate[date] = (queriesByDate[date] || 0) + 1;
        });

        const sortedDates = Object.keys(queriesByDate).sort((a,b) => new Date(a).getTime() - new Date(b).getTime());
        const chartLabels = sortedDates.map(date => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
        const chartDataPoints = sortedDates.map(date => queriesByDate[date]);
        const backgroundColors = sortedDates.map(date => (logFilterDate && date === logFilterDate) ? 'hsla(10, 80%, 60%, 0.8)' : 'rgba(75, 192, 192, 0.7)');
        const borderColors = sortedDates.map(date => (logFilterDate && date === logFilterDate) ? 'hsla(10, 80%, 60%, 1)' : 'rgba(75, 192, 192, 1)');

        setLogQueriesChartData({
          labels: chartLabels,
          datasets: [{
            label: 'Queries per Day',
            data: chartDataPoints,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 1,
            originalDates: sortedDates,
          }] as any,
        });
      }
    } else if (activeTab === 1) {
      setLogQueriesChartData({ labels: [], datasets: [] });
      setUserQueriesTreemapData(null);
    }
  }, [fetchedLogs, activeTab, selectedUsernameLog, logFilterDate, logFilterCategory, logFilterUser]);

  // useEffect to prepare data for the question category queries chart (part of logs tab)
  useEffect(() => {
    if (!fetchedLogs) return;
    if (activeTab === 1 && fetchedLogs.length > 0) {
      const logsForCategoryChart = fetchedLogs.filter(log => {
        const usernameMatch = selectedUsernameLog.length === 0 || selectedUsernameLog.includes(log.username || log.user_email || '');
        const dateFilterMatch = !logFilterDate || new Date(log.timestamp).toLocaleDateString('en-CA') === logFilterDate;
        const userIdentifier = log.username || log.user_email || '';
        const userFilterMatch = !logFilterUser || 
            (logFilterUser === 'Other Users' 
                ? userIdentifier && !top10LogUsers.includes(userIdentifier)
                : userIdentifier === logFilterUser);
        return usernameMatch && dateFilterMatch && userFilterMatch;
      });

      if (logsForCategoryChart.length === 0) {
        setQuestionCategoryChartData({ labels: [], datasets: [] });
        setQuestionCategoryExplanations({});
        return;
      }

      const queriesByCategory: { [category: string]: number } = {};
      const explanationsMap: { [category: string]: string } = {};

      logsForCategoryChart.forEach(log => {
        const category = log.question_category || "Uncategorized";
        queriesByCategory[category] = (queriesByCategory[category] || 0) + 1;
        if (log.question_category_explanation && !explanationsMap[category]) {
          explanationsMap[category] = log.question_category_explanation;
        }
      });
      setQuestionCategoryExplanations(explanationsMap);

      const sortedCategoriesData = Object.entries(queriesByCategory)
        .sort(([, aCount], [, bCount]) => bCount - aCount)
        .map(([name, count]) => ({ name, count }));

      const backgroundColors = sortedCategoriesData.map(item => 
        (logFilterCategory && item.name === logFilterCategory) ? 'hsla(10, 80%, 60%, 0.8)' : 'rgba(255, 159, 64, 0.7)'
      );

      setQuestionCategoryChartData({
        labels: sortedCategoriesData.map(item => item.name),
        datasets: [{
          label: 'Queries',
          data: sortedCategoriesData.map(item => item.count),
          backgroundColor: backgroundColors,
        }]
      });
    } else if (activeTab === 1) {
      setQuestionCategoryChartData({ labels: [], datasets: [] });
      setQuestionCategoryExplanations({});
    }
  }, [fetchedLogs, activeTab, selectedUsernameLog, logFilterCategory, logFilterDate, logFilterUser, top10LogUsers]);

  // Color palette for question categories in the chart
  const categoryColors = [
    'rgba(255, 99, 132, 0.7)', 'rgba(54, 162, 235, 0.7)', 'rgba(255, 206, 86, 0.7)',
    'rgba(75, 192, 192, 0.7)', 'rgba(153, 102, 255, 0.7)', 'rgba(255, 159, 64, 0.7)',
    'rgba(199, 199, 199, 0.7)', 'rgba(83, 102, 255, 0.7)', 'rgba(40, 159, 64, 0.7)',
    'rgba(210, 99, 132, 0.7)', 'rgba(64, 159, 255, 0.7)', 'rgba(255, 102, 86, 0.7)'
    // Add more colors if you have more categories
  ];

  // Star labels for the rating chart
  const starRatingLabels = ['★', '★★', '★★★', '★★★★', '★★★★★'];
  // useEffect to prepare data for the rating distribution chart
  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    if (!fetchedFeedbacks) return;
    if (activeTab === 0 && fetchedFeedbacks.length > 0) {
        let itemsToChart = fetchedFeedbacks;

        // --- Filter Data ---
        if (selectedAppVersion !== "all" && selectedAppVersion) {
            itemsToChart = itemsToChart.filter(item => item.app_version === selectedAppVersion);
        }

        if (selectedQuestionCategory !== "all" && selectedQuestionCategory) {
            itemsToChart = itemsToChart.filter(
                item => (item.question_category || "Uncategorized") === selectedQuestionCategory
            );
        }

        if (selectedUsernameFeedback.length > 0) {
            itemsToChart = itemsToChart.filter(item => selectedUsernameFeedback.includes(item.username || item.user_email || ''));
        }

        // --- Process Data ---
        const ratingsByCategory: { [category: string]: { [rating: number]: number } } = {};
        const currentFeedbackCategoryExplanations: { [key: string]: string } = {};

        itemsToChart.forEach(item => {
            const category = item.question_category || "Uncategorized";
            const ratingNum = typeof item.rating === 'string' ? parseInt(item.rating, 10) : item.rating;
            const explanation = item.question_category_explanation || "";

            if (!ratingsByCategory[category]) {
                ratingsByCategory[category] = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
            }
            if (explanation && explanation.trim() !== "") {
                currentFeedbackCategoryExplanations[category] = explanation;
            }

            if (!isNaN(ratingNum) && ratingNum >= 1 && ratingNum <= 5) {
                ratingsByCategory[category][ratingNum]++;
            }
        });

        setFeedbackCategoryExplanations(currentFeedbackCategoryExplanations);
        const sortedCategories = Object.keys(ratingsByCategory).sort();

        // --- Generate Chart Data based on View Mode ---
        if (chartViewMode === 'categories') {
            if (categoryChartViewMode === 'continuous') { // Average rating view
                const categoryData = sortedCategories.map(category => {
                    const counts = ratingsByCategory[category];
                    let totalRating = 0;
                    let totalVotes = 0;
                    for (let i = 1; i <= 5; i++) {
                        totalRating += i * (counts[i] || 0);
                        totalVotes += (counts[i] || 0);
                    }
                    const averageRating = totalVotes > 0 ? totalRating / totalVotes : 0;
                    return { category, averageRating, totalVotes };
                });

                const allVotes = categoryData.map(d => d.totalVotes);
                const minVotes = allVotes.length > 0 ? Math.min(...allVotes) : 0;
                const maxVotes = allVotes.length > 0 ? Math.max(...allVotes) : 0;
                setMinFeedbackVotes(minVotes);
                setMaxFeedbackVotes(maxVotes);

                const getColorForVotes = (votes: number): string => {
                    if (votes === 0) return 'rgba(230, 230, 230, 0.7)';
                    if (maxVotes === minVotes) return 'hsla(210, 70%, 65%, 0.7)';
                    const percentage = (votes - minVotes) / (maxVotes - minVotes);
                    const lightness = 90 - (percentage * 50);
                    return `hsla(210, 70%, ${lightness}%, 0.7)`;
                };

                setRatingChartData({
                    labels: categoryData.map(d => d.category),
                    datasets: [{
                        label: 'Average Rating',
                        data: categoryData.map(d => d.averageRating),
                        backgroundColor: categoryData.map(d => (chartFilterCategory && d.category === chartFilterCategory) ? 'hsla(10, 80%, 60%, 0.8)' : getColorForVotes(d.totalVotes)),
                        borderColor: categoryData.map(d => (chartFilterCategory && d.category === chartFilterCategory) ? 'hsla(10, 80%, 60%, 1)' : getColorForVotes(d.totalVotes).replace('0.7', '1')),
                        borderWidth: 1,
                        voteCounts: categoryData.map(d => d.totalVotes)
                    } as any],
                });
            } else { // 'discrete' view (stacked bar chart)
                setMinFeedbackVotes(0); // No gradient legend for this view
                setMaxFeedbackVotes(0);

                const starRatingColors = [ // These colors are for the stacked discrete stars
                    'rgba(214, 40, 40, 0.7)',  // 1 Star (Red)
                    'rgba(247, 127, 0, 0.7)',  // 2 Stars (Orange)
                    'rgba(252, 191, 73, 0.7)', // 3 Stars (Yellow)
                    'rgba(132, 204, 168, 0.7)',// 4 Stars (Light Green)
                    'rgba(5, 150, 105, 0.7)'   // 5 Stars (Dark Green)
                ];

                const datasets = [1, 2, 3, 4, 5].map((star, index) => ({ // Use star symbols for labels
                    label: starRatingLabels[index],
                    data: sortedCategories.map(category => ratingsByCategory[category][star] || 0),
                    backgroundColor: starRatingColors[index],
                    borderWidth: 1,
                }));

                setRatingChartData({
                    labels: sortedCategories,
                    datasets: datasets as any,
                });
            }
        } else { // chartViewMode === 'stars'
            setMinFeedbackVotes(0);
            setMaxFeedbackVotes(0);

            const datasets = sortedCategories.map((category, index) => {
                const counts = ratingsByCategory[category];
                const explanationForDataset = currentFeedbackCategoryExplanations[category] || "No explanation available.";
                const backgroundColors = starRatingLabels.map((_, starIndex) => {
                    const currentRating = starIndex + 1;
                    if (chartFilterCategory === category && chartFilterStar === currentRating) {
                        return 'hsla(10, 80%, 60%, 0.8)'; // Highlight color
                    }
                    return categoryColors[index % categoryColors.length];
                });
                return {
                    label: category,
                    data: [counts[1] || 0, counts[2] || 0, counts[3] || 0, counts[4] || 0, counts[5] || 0],
                    backgroundColor: backgroundColors,
                    explanation: explanationForDataset,
                    borderWidth: 1,
                };
            });
            setRatingChartData({
                labels: starRatingLabels,
                datasets: datasets,
            });
        }
    } else if (activeTab === 0) { // Clear chart if no feedbacks
        setRatingChartData({ labels: starRatingLabels, datasets: [] });
    }
}, [fetchedFeedbacks, selectedAppVersion, selectedQuestionCategory, selectedUsernameFeedback, activeTab, chartViewMode, barStackMode, categoryChartViewMode, chartFilterCategory, chartFilterStar]); // Added new dependencies

  const getChartOptions = (aggregation: 'month' | 'day'): ChartOptions<'bar'> => ({
    responsive: true,
    maintainAspectRatio: false, // Important for fitting chart in a fixed-height box
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
          callbacks: {
            label: function(context) {
              const lines: string[] = [];
              let firstLine = context.dataset.label || '';
              if (firstLine) {
                  firstLine += ': ';
              }
              if (context.parsed.y !== null) {
                  const value = context.parsed.y;
                  // Check if the dataset is the line chart for requests (using 'as any' to satisfy TypeScript for mixed chart types)
                  if ((context.dataset as any).type === 'line') {
                      firstLine += new Intl.NumberFormat('en-US').format(value);
                  } else { // It's a bar chart for costs
                    if (costViewMode === 'cost') {
                      firstLine += new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
                    } else {
                      firstLine += new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(value);
                    }
                  }
              }
              lines.push(firstLine);

              // Only show total for period if in 'cost' mode and it's a bar chart element
              if (costViewMode === 'cost' && (context.dataset as any).type !== 'line') {
                  const dataIndex = context.dataIndex;
                  let periodTotalInCents = 0;
                  if (context.chart.data.datasets) {
                      context.chart.data.datasets.forEach(dataset => {
                          // Only sum up costs from bar charts, ignore the line chart for requests
                          if (dataset.type === 'bar') {
                            const value = dataset.data[dataIndex] as number;
                            if (typeof value === 'number') {
                                periodTotalInCents += Math.round(value * 100);
                            }
                          }
                      });
                    }
                  const aggregationPeriodText = aggregation === 'month' ? 'month' : 'day';
                  const periodTotal = periodTotalInCents / 100;
                  lines.push(`(Total for ${aggregationPeriodText}: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(periodTotal)})`);
              }
              return lines;
            }
          }
      }
    },
    scales: {
      x: {
        stacked: true,
        title: { display: true, text: 'Date' }
      },
      'y-cost': {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        stacked: true,
        title: { display: true, text: costViewMode === 'cost' ? 'Cost (USD)' : 'Avg Cost per Request (USD)' },
        ticks: {
          callback: value => {
            if (typeof value !== 'number') return value;
            if (costViewMode === 'cost') {
              return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value);
            }
            return new Intl.NumberFormat('en-US', { style: 'currency', 'currency': 'USD', minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(value);
          }
        },
        beginAtZero: true,
      },
      'y-requests': {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Total Requests',
        },
        grid: {
          drawOnChartArea: false, // only draw grid for primary axis
        },
        ticks: {
            precision: 0, // Ensure integer ticks
        },
        beginAtZero: true,
      }
    }
  });

  const getRatingDistributionChartOptions = (): ChartOptions<'bar'> => {
    const commonOptions: ChartOptions<'bar'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: chartViewMode === 'stars' || (chartViewMode === 'categories' && categoryChartViewMode === 'discrete'),
          position: 'top' as const,
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const lines: string[] = [];
              const categoryName = context.label || '';

              if (chartViewMode === 'categories') {
                if (categoryChartViewMode === 'continuous') {
                  const averageRating = context.parsed.y;
                  const voteCount = (context.dataset as any).voteCounts?.[context.dataIndex];

                  lines.push(`${categoryName}: ${averageRating.toFixed(2)} ${renderRatingStars(averageRating)}`);
                  if (voteCount !== undefined) {
                      lines.push(`(${voteCount} vote${voteCount === 1 ? '' : 's'})`);
                  }
                } else { // 'discrete' stacked view
                  const starRatingLabel = context.dataset.label || '';
                  const voteCount = context.parsed.y;
                  lines.push(`${starRatingLabel}: ${voteCount} votes`);
                }

                // Add explanation for both category views
                const categoryExplanation = feedbackCategoryExplanations[categoryName];
                if (categoryExplanation && categoryExplanation.trim() !== "" && categoryExplanation !== "No explanation available.") {
                  lines.push(`(${categoryExplanation})`);
                }

              } else { // chartViewMode === 'stars'
                // Stacked/Grouped mode
                lines.push(`${context.dataset.label || 'Category'}: ${context.parsed.y} votes`);
                const explanation = (context.dataset as any).explanation; // Access custom 'explanation' property
                if (explanation && explanation.trim() !== "" && explanation !== "No explanation available.") {
                  lines.push(`(${explanation})`);
                }
              }
              return lines;
            }
          }
        }
      },
      onClick: (event: any, elements: any[], chart: any) => {
        if (elements.length === 0) {
          // If clicking outside a bar, clear filters
          setChartFilterCategory(null);
          setChartFilterStar(null);
          return;
        }

        const element = elements[0];
        const { datasetIndex, index } = element;
        const data = chart.data;

        if (chartViewMode === 'stars') {
            const clickedStarRating = index + 1;
            const clickedCategory = data.datasets[datasetIndex].label || null;

            if (chartFilterStar === clickedStarRating && chartFilterCategory === clickedCategory) {
                setChartFilterCategory(null);
                setChartFilterStar(null);
            } else {
                setChartFilterCategory(clickedCategory);
                setChartFilterStar(clickedStarRating);
            }
        } else { // 'categories'
            const clickedCategory = data.labels?.[index] as string | null;
            // Toggle filter: if same category is clicked again, clear filter
            setChartFilterCategory(prev => prev === clickedCategory ? null : clickedCategory);
            setChartFilterStar(null); // Always reset star filter in this mode
        }
      },
      scales: {
        x: {
          stacked: (chartViewMode === 'stars' && barStackMode === 'stacked') || (chartViewMode === 'categories' && categoryChartViewMode === 'discrete'),
          title: { display: true, text: chartViewMode === 'stars' ? 'Rating' : 'Question Category' },
        },
        y: {
          stacked: (chartViewMode === 'stars' && barStackMode === 'stacked') || (chartViewMode === 'categories' && categoryChartViewMode === 'discrete'),
          title: { 
            display: true, 
            text: chartViewMode === 'stars' || (chartViewMode === 'categories' && categoryChartViewMode === 'discrete')
              ? 'Number of Votes'
              : 'Average Rating'
          },
          beginAtZero: true,
          ticks: {
            stepSize: chartViewMode === 'stars' || (chartViewMode === 'categories' && categoryChartViewMode === 'discrete') ? undefined : 0.5, // Let chartjs decide step for counts
            precision: chartViewMode === 'stars' || (chartViewMode === 'categories' && categoryChartViewMode === 'discrete') ? 0 : 1,
          }
        }
      }
    };
    return commonOptions;
  };

  const getLogQueriesChartOptions = (logFilterDate: string | null): ChartOptions<'bar'> => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide legend if only one dataset
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `Queries: ${context.parsed.y}`;
          }
        }
      }
    },
    onClick: (event: any, elements: any[], chart: any) => {
        if (elements.length === 0) {
            setLogFilterDate(null);
            return;
        }
        const element = elements[0];
        const { index } = element;
        const originalDate = chart.data.datasets[0].originalDates?.[index] as string | null;

        if (originalDate) {
            setLogFilterDate(prev => prev === originalDate ? null : originalDate);
        }
    },
    scales: {
      x: { title: { display: true, text: 'Date' } },
      y: { title: { display: true, text: 'Number of Queries' }, beginAtZero: true, ticks: { stepSize: 1, precision: 0 } },
    }
  });

  const getQuestionCategoryChartOptions = (explanations: { [key: string]: string }, logFilterCategory: string | null): ChartOptions<'bar'> => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y', // Horizontal bar chart
    plugins: {
      legend: {
        display: false, // Only one dataset
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const categoryName = context.label; // Category name from y-axis
            const queryCount = context.parsed.x; // Value from x-axis
            const lines = [];

            lines.push(`Queries: ${queryCount}`); // Line 1: Queries: n

            const explanation = explanations[categoryName];
            if (explanation && explanation.trim() !== "") {
              lines.push(`(${explanation})`);
            }
            return lines;
          }
        }
      }
    },
    onClick: (event: any, elements: any[], chart: any) => {
        if (elements.length === 0) {
            setLogFilterCategory(null);
            return;
        }
        const element = elements[0];
        const { index } = element;
        const categoryName = chart.data.labels?.[index] as string | null;

        if (categoryName) {
            setLogFilterCategory(prev => prev === categoryName ? null : categoryName);
        }
    },
    scales: {
      x: { title: { display: true, text: 'Number of Queries' }, beginAtZero: true, ticks: { stepSize: 1, precision: 0 } },
      y: { title: { display:false } }, // Category labels are on y-axis
    }
  });

  const getScatterChartOptions = (): ChartOptions<'scatter'> => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'top' as const,
            labels: {
                boxWidth: 15,
                padding: 15,
            }
        },
        title: {
            display: true,
            text: costViewMode === 'cost'
                ? 'Daily Cost vs. Requests (dot size by Avg Cost/Req)'
                : 'Daily Avg Cost/Req vs. Requests (dot size by Total Cost)'
        },
        tooltip: {
            callbacks: {
                label: function(context) {
                    const point = context.raw as any;
                    const lines = [];
                    lines.push(`Service (dominant): ${context.dataset.label}`);
                    lines.push(`Date: ${point.date}`);
                    lines.push(`Requests: ${new Intl.NumberFormat('en-US').format(point.requests)}`);
                    lines.push(`Total Cost: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(point.totalCost)}`);
                    lines.push(`Avg Cost/Req: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(point.avgCost)}`);
                    return lines;
                }
            }
        },
    },
    scales: {
        x: { // Dynamically set x-axis type based on logScaleRequestsScatter state
            type: logScaleRequestsScatter ? 'logarithmic' : 'linear' as const,
            position: 'bottom',
            title: { display: true, text: 'Total Requests' },
            min: logScaleRequestsScatter ? 1 : undefined, // Start at 1 for log scale
            ticks: {
                callback: value => new Intl.NumberFormat('en-US', { notation: 'compact', compactDisplay: 'short' }).format(value as number)
            }
        },
        y: {
            title: {
                display: true,
                text: costViewMode === 'cost' ? 'Total Cost (USD)' : 'Avg Cost per Request (USD)'
            },
            ticks: {
                callback: value => {
                    if (typeof value !== 'number') return value;
                    if (costViewMode === 'cost') {
                        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
                    } else {
                        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(value);
                    }
                }
            }
        }
    }
  });

  return (
    <Stack direction="column" sx={{ height: "100%", boxSizing: "border-box", overflow: 'hidden' }}>
      {/* Horizontal Tabs Menu - sticky at top */}
      <Paper elevation={1} sx={{ flexShrink: 0, zIndex: 1 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="admin sections tabs" centered={false} variant="scrollable" scrollButtons="auto">
          <Tab label="Feedback" />
          <Tab label="Log" />
          <Tab label="Cost" />
        </Tabs>
      </Paper>

      {/* Content Area based on Active Tab */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 1 }}>
        {/* Tab 0: Feedbacks List */}
        {activeTab === 0 && (() => {
          const displayedFeedbacks = (fetchedFeedbacks || []).filter(item => {
            const versionMatch = selectedAppVersion === "all" || !selectedAppVersion || item.app_version === selectedAppVersion;
            const categoryMatch = selectedQuestionCategory === "all" || !selectedQuestionCategory || (item.question_category || "Uncategorized") === selectedQuestionCategory;
            const usernameMatch = selectedUsernameFeedback.length === 0 || selectedUsernameFeedback.includes(item.username || item.user_email || '');
            
            const chartCategoryFilterMatch = !chartFilterCategory || (item.question_category || "Uncategorized") === chartFilterCategory;
            const ratingNum = typeof item.rating === 'string' ? parseInt(item.rating, 10) : (item.rating || 0);
            const chartStarFilterMatch = !chartFilterStar || ratingNum === chartFilterStar;

            return versionMatch && categoryMatch && usernameMatch && chartCategoryFilterMatch && chartStarFilterMatch;
          });

          return (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <Paper elevation={3} sx={{ p: 2, mb: 2, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Rating Analysis by Question Category</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <ToggleButtonGroup
                        color="primary"
                        value={chartViewMode}
                        exclusive
                        onChange={(event, newMode) => {
                            if (newMode !== null) {
                                if (newMode === 'categories') {
                                    setCategoryChartViewMode('discrete');
                                }
                                setChartViewMode(newMode as 'stars' | 'categories');
                            }
                        }}
                        aria-label="chart view mode"
                        size="small"
                    >
                        <ToggleButton value="stars" aria-label="view by stars">Stars</ToggleButton>
                        <ToggleButton value="categories" aria-label="view by categories">Categories</ToggleButton>
                    </ToggleButtonGroup>

                    {chartViewMode === 'categories' && (
                        <ToggleButtonGroup
                            color="primary" value={categoryChartViewMode} exclusive
                            onChange={(event, newMode) => { if (newMode !== null) { setCategoryChartViewMode(newMode as 'discrete' | 'continuous'); } }}
                            aria-label="category chart view mode" size="small"
                        >
                            <ToggleButton value="discrete" aria-label="discrete view">Discrete</ToggleButton>
                            <ToggleButton value="continuous" aria-label="continuous view">Continuous</ToggleButton>
                        </ToggleButtonGroup>
                    )}

                    {chartViewMode === 'stars' && (
                        <ToggleButtonGroup
                            color="primary"
                            value={barStackMode}
                            exclusive
                            onChange={(event, newMode) => {
                                if (newMode !== null) {
                                    setBarStackMode(newMode as 'stacked' | 'grouped');
                                }
                            }}
                            aria-label="bar display mode"
                            size="small"
                        >
                            <ToggleButton value="stacked" aria-label="stacked view">Stacked</ToggleButton>
                            <ToggleButton value="grouped" aria-label="grouped view">Grouped</ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </Box>
            </Box>
            <Box sx={{ height: '350px', width: '100%' }}>
                {isLoadingFeedbacks && <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 5 }} />}
                {feedbackFetchError && !isLoadingFeedbacks && <Typography color="error" sx={{ textAlign: 'center', pt: 5 }}>{feedbackFetchError.message}</Typography>}
                {!isLoadingFeedbacks && !feedbackFetchError && (
                    (ratingChartData.datasets.length > 0 && ratingChartData.datasets.some(ds => ds.data.some(d => typeof d === 'number' && d > 0))) ? (
                        <Bar key={`rating-${isDark}`} options={getRatingDistributionChartOptions()} data={ratingChartData} />
                    ) : (
                        <Typography sx={{ textAlign: 'center', pt: 5 }}>No rating data available for the selected criteria.</Typography>
                    )
                )}
            </Box>
            {chartViewMode === 'categories' && categoryChartViewMode === 'continuous' && <GradientLegend min={minFeedbackVotes} max={maxFeedbackVotes} />}
          </Paper>
          
          {(chartFilterCategory || chartFilterStar) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pl: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Active chart filter:</Typography>
                <Chip
                    label={
                        `${chartFilterCategory ? `Category: "${chartFilterCategory}"` : ''}` +
                        `${chartFilterCategory && chartFilterStar ? ' & ' : ''}` +
                        `${chartFilterStar ? `Rating: ${renderRatingStars(chartFilterStar)}` : ''}`
                    }
                    onDelete={() => {
                        setChartFilterCategory(null);
                        setChartFilterStar(null);
                    }}
                    color="primary"
                    variant="outlined"
                    size="small"
                />
            </Box>
          )}

          <Paper elevation={3} sx={{ p: 2, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
              <Box>
                <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>User Feedbacks</Typography>
                <Typography variant="caption" color="text.secondary">Showing data from the last 2 months</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {allUsernamesFeedback.length > 0 && (
                  <FormControl sx={{ m: 1, minWidth: 180 }} size="small">
                    <InputLabel id="username-feedback-filter-label">
                      {selectedUsernameFeedback.length === 0 ? "All Users" : "Username"}
                    </InputLabel>
                    <Select
                      labelId="username-feedback-filter-label"
                      id="username-feedback-filter"
                      multiple
                      value={selectedUsernameFeedback}
                      onChange={(e) => {
                        const value = e.target.value as string[];
                        if (value.includes('all')) {
                          setSelectedUsernameFeedback([]);
                        } else {
                          setSelectedUsernameFeedback(value);
                        }
                      }}
                      renderValue={(selected) => selected.length === 0 ? "All Users" : selected.join(', ')}
                    >
                      <MenuItem value="all">
                        <Checkbox checked={selectedUsernameFeedback.length === 0} />
                        <ListItemText primary="All Users" />
                      </MenuItem>
                      {allUsernamesFeedback.map(username => (
                        <MenuItem key={username} value={username}>
                          <Checkbox checked={selectedUsernameFeedback.indexOf(username) > -1} />
                          <ListItemText primary={username} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {allQuestionCategories.length > 0 && (
                  <FormControl sx={{ m: 1, minWidth: 180 }} size="small">
                    <InputLabel id="question-category-filter-label">Question Category</InputLabel>
                    <Select
                      labelId="question-category-filter-label"
                      id="question-category-filter"
                      value={selectedQuestionCategory}
                      label="Question Category"
                      onChange={(e) => setSelectedQuestionCategory(e.target.value as string)}
                    >
                      <MenuItem value="all">All Categories</MenuItem>
                      {allQuestionCategories.map(category => (
                        <MenuItem key={category} value={category}>{category}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {allAppVersions.length > 0 && (
                  <FormControl sx={{ m: 1, minWidth: 150 }} size="small">
                    <InputLabel id="app-version-filter-label">App Version</InputLabel>
                    <Select
                      labelId="app-version-filter-label"
                      id="app-version-filter"
                      value={selectedAppVersion}
                      label="App Version"
                      onChange={(e) => setSelectedAppVersion(e.target.value as string)}
                    >
                      <MenuItem value="all">All Versions</MenuItem>
                      {allAppVersions.map(version => (
                        <MenuItem key={version} value={version}>{version}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Box>
            {isLoadingFeedbacks && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}
            {feedbackFetchError && <Typography color="error">{feedbackFetchError.message}</Typography>}
            {!isLoadingFeedbacks && !feedbackFetchError && (
              displayedFeedbacks.length > 0 ? (
                <>
                <TableContainer component={Paper} sx={{ flex: 1, overflowY: 'auto' }}>
                  <Table stickyHeader aria-label="user feedback table" size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Timestamp</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Question</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Rating</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Fixed?</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedFeedbacks.map((item, index) => (
                        <TableRow
                          key={item.timestamp + (item.user_email || '') + index}
                          hover
                          onClick={() => setSelectedFeedback(item)}
                          sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {new Date(item.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.username || item.user_email || 'N/A'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.question_category || 'N/A'}</TableCell>
                          <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {truncateText(item.question, 80)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap', fontSize: '1.1rem' }}>
                            {renderRatingStars(item.rating)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {item.is_fixed == null ? 'N/A' : Number(item.is_fixed) === 1 ? 'Yes' : 'No'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {/* Feedback Detail Dialog */}
                <Dialog open={!!selectedFeedback} onClose={() => setSelectedFeedback(null)} maxWidth="md" fullWidth scroll="paper">
                  {selectedFeedback && (
                    <>
                      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>Feedback Detail</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(selectedFeedback.timestamp).toLocaleString()} — {selectedFeedback.username || selectedFeedback.user_email || 'N/A'}
                          </Typography>
                        </Box>
                        <IconButton onClick={() => setSelectedFeedback(null)} size="small"><CloseIcon /></IconButton>
                      </DialogTitle>
                      <DialogContent dividers>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                          <DetailRow label="Category">
                            <Typography variant="body2">{selectedFeedback.question_category || 'N/A'}</Typography>
                            {selectedFeedback.question_category_explanation && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {selectedFeedback.question_category_explanation}
                              </Typography>
                            )}
                          </DetailRow>
                          <DetailRow label="Follow-up?">
                            <Typography variant="body2">
                              {selectedFeedback.is_question_a_follow_up_question == null ? 'N/A' : Number(selectedFeedback.is_question_a_follow_up_question) === 1 ? 'Yes' : 'No'}
                            </Typography>
                          </DetailRow>
                          <DetailRow label="IP Address">
                            <Typography variant="body2">{selectedFeedback.user_ip || 'N/A'}</Typography>
                          </DetailRow>
                          <DetailRow label="Fixed?">
                            <Typography variant="body2">
                              {selectedFeedback.is_fixed == null ? 'N/A' : Number(selectedFeedback.is_fixed) === 1 ? 'Yes' : 'No'}
                            </Typography>
                          </DetailRow>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <DetailRow label="Question">
                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: isDark ? 'background.default' : '#f9f9f9' }}>
                            <Typography variant="body2" component="div" dangerouslySetInnerHTML={{ __html: selectedFeedback.question }} />
                          </Paper>
                        </DetailRow>
                        <DetailRow label="Answer">
                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: isDark ? 'background.default' : '#f9f9f9', maxHeight: 400, overflowY: 'auto' }}>
                            <Box
                              sx={{
                                '& p': { mt: 0, mb: '0.5em' },
                                '& h1, & h2, & h3, & h4': { mt: '1em', mb: '0.5em', fontWeight: 'bold', fontSize: '1.1rem' },
                                '& ul, & ol': { mt: 0, mb: '0.5em', pl: '1.5em' },
                                '& p:empty': { display: 'none' },
                                '& li:empty': { display: 'none' },
                                fontSize: '0.875rem',
                              }}
                            >
                              <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkBreaks]}>
                                {selectedFeedback.answer}
                              </ReactMarkdown>
                            </Box>
                          </Paper>
                        </DetailRow>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' }, gap: 2 }}>
                          <DetailRow label="Rating">
                            <Typography sx={{ fontSize: '1.3rem' }}>{renderRatingStars(selectedFeedback.rating)}</Typography>
                          </DetailRow>
                          <DetailRow label="User Feedback">
                            <Typography variant="body2">{selectedFeedback.feedback || 'No additional feedback.'}</Typography>
                          </DetailRow>
                        </Box>
                        {selectedFeedback.fix_details && (
                          <DetailRow label="Fix Details">
                            <Typography variant="body2">{selectedFeedback.fix_details}</Typography>
                          </DetailRow>
                        )}
                      </DialogContent>
                    </>
                  )}
                </Dialog>
                </>
              ) : (
                <Typography sx={{ mt: 2, textAlign: 'center' }}>No feedback submitted yet.</Typography>
              )
            )}            
          </Paper>
          </Box>
          );
        })()}
        
        {/* Tab 1: System Logs */}
        {activeTab === 1 && (() => {
          const displayedLogs = (fetchedLogs || []).filter(log => {
            const usernameMatch = selectedUsernameLog.length === 0 || selectedUsernameLog.includes(log.username || log.user_email || '');
            
            const dateFilterMatch = !logFilterDate || new Date(log.timestamp).toLocaleDateString('en-CA') === logFilterDate;
            const categoryFilterMatch = !logFilterCategory || (log.question_category || 'Uncategorized') === logFilterCategory;
            
            const userIdentifier = log.username || log.user_email || '';
            const userFilterMatch = !logFilterUser || 
                (logFilterUser === 'Other Users' 
                    ? userIdentifier && !top10LogUsers.includes(userIdentifier)
                    : userIdentifier === logFilterUser);

            return usernameMatch && dateFilterMatch && categoryFilterMatch && userFilterMatch;
          });

          return (
            <Paper elevation={3} sx={{ p: 2, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Grid container spacing={2} sx={{ mb: 3, flexShrink: 0 }}>
                <Grid item xs={12} md={4}>
                  <Paper elevation={1} sx={{ p: 2, height: '350px' }}>
                     <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>Queries per Day</Typography>
                    <Box sx={{ height: 'calc(100% - 30px)', width: '100%' }}>
                      {isLoadingLogs && <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 5 }} />}
                      {logFetchError && !isLoadingLogs && <Typography color="error" sx={{ textAlign: 'center', pt: 5 }}>Error loading log data.</Typography>}
                      {!isLoadingLogs && !logFetchError && (
                        logQueriesChartData.datasets.length > 0 && logQueriesChartData.datasets[0]?.data?.length > 0 ? (
                            <Bar key={`log-queries-${isDark}`} options={getLogQueriesChartOptions(logFilterDate)} data={logQueriesChartData} />
                        ) : (
                            <Typography sx={{ textAlign: 'center', pt: 5 }}>
                              No query data available to display chart.
                            </Typography>
                        )
                      )}
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper elevation={1} sx={{ p: 2, height: '350px' }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>Queries by Category</Typography>
                    <Box sx={{ height: 'calc(100% - 30px)', width: '100%' }}>
                      {isLoadingLogs && <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 5 }} />}
                      {logFetchError && !isLoadingLogs && <Typography color="error" sx={{ textAlign: 'center', pt: 5 }}>Error loading category data.</Typography>}
                      {!isLoadingLogs && !logFetchError && (
                          questionCategoryChartData.datasets.length > 0 && questionCategoryChartData.datasets[0]?.data?.length > 0 ? (
                              <Bar key={`category-${isDark}`} options={getQuestionCategoryChartOptions(questionCategoryExplanations, logFilterCategory)} data={questionCategoryChartData} />
                          ) : (
                              <Typography sx={{ textAlign: 'center', pt: 5 }}>
                                No category data to display.
                              </Typography>
                          )
                      )}
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper elevation={1} sx={{ p: 2, height: '350px' }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>Total Queries by User</Typography>
                    <Box sx={{ height: 'calc(100% - 30px)', width: '100%' }}>
                      {isLoadingLogs && <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 5 }} />}
                      {logFetchError && !isLoadingLogs && <Typography color="error" sx={{ textAlign: 'center', pt: 5 }}>Error loading user query data.</Typography>}
                      {!isLoadingLogs && !logFetchError && (
                          userQueriesTreemapData && userQueriesTreemapData.children.length > 0 ? (
                              <ResponsiveTreeMap
                                data={userQueriesTreemapData}
                                identity="name"
                                value="loc"
                                valueFormat="d"
                                margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                                onClick={(node) => {
                                  const userName = node.data.name as string | null;
                                  if (userName) {
                                      setLogFilterUser(prev => (prev === userName ? null : userName));
                                  }
                                }}
                                borderColor={(node) => {
                                  if (logFilterUser && node.data.name === logFilterUser) {
                                      return 'hsl(10, 80%, 50%)';
                                  }
                                  return isDark ? '#1e1e1e' : 'white';
                                }}
                                labelSkipSize={12}
                                labelTextColor={{ from: 'color', modifiers: [[isDark ? 'brighter' : 'darker', 1.2]], }}
                                parentLabelPosition="left"
                                parentLabelTextColor={{ from: 'color', modifiers: [[isDark ? 'brighter' : 'darker', 2]], }}
                                colors={{ scheme: isDark ? 'dark2' : 'nivo' }}
                              />
                          ) : (
                              <Typography sx={{ textAlign: 'center', pt: 5 }}>
                                No user query data to display.
                              </Typography>
                          )
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
              {(logFilterDate || logFilterCategory || logFilterUser) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pl: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Active chart filter:</Typography>
                    <Chip
                        label={
                            [
                                logFilterDate ? `Date: "${new Date(logFilterDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}"` : '',
                                logFilterCategory ? `Category: "${logFilterCategory}"` : '',
                                logFilterUser ? `User: "${logFilterUser}"` : ''
                            ].filter(Boolean).join(' & ')
                        }
                        onDelete={() => {
                            setLogFilterDate(null);
                            setLogFilterCategory(null);
                            setLogFilterUser(null);
                        }}
                        color="primary"
                        variant="outlined"
                        size="small"
                    />
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                <Box>
                  <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>User Interaction Logs</Typography>
                  <Typography variant="caption" color="text.secondary">Showing data from the last 2 months</Typography>
                </Box>
                {allUsernamesLog.length > 0 && (
                  <FormControl sx={{ m: 1, minWidth: 180 }} size="small">
                    <InputLabel id="username-log-filter-label">
                      {selectedUsernameLog.length === 0 ? "All Users" : "Username"}
                    </InputLabel>
                    <Select
                      labelId="username-log-filter-label"
                      id="username-log-filter"
                      multiple
                      value={selectedUsernameLog}
                      onChange={(e) => {
                        const value = e.target.value as string[];
                        if (value.includes('all')) {
                          setSelectedUsernameLog([]);
                        } else {
                          setSelectedUsernameLog(value);
                        }
                      }}
                      renderValue={(selected) => selected.length === 0 ? "All Users" : selected.join(', ')}
                    >
                      <MenuItem value="all">
                        <Checkbox checked={selectedUsernameLog.length === 0} />
                        <ListItemText primary="All Users" />
                      </MenuItem>
                      {allUsernamesLog.map(username => (
                        <MenuItem key={username} value={username}>
                          <Checkbox checked={selectedUsernameLog.indexOf(username) > -1} />
                          <ListItemText primary={username} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              {isLoadingLogs && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}
              {logFetchError && <Typography color="error">{logFetchError.message}</Typography>}
              {!isLoadingLogs && !logFetchError && (
                displayedLogs.length > 0 ? (
                  <>
                  <TableContainer component={Paper} sx={{ flex: 1, overflowY: 'auto' }}>
                    <Table stickyHeader aria-label="user logs table" size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Timestamp</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Question</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Follow-up?</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Image?</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayedLogs.map((log, index) => (
                          <TableRow
                            key={`${log.timestamp}-${index}`}
                            hover
                            onClick={() => setSelectedLog(log)}
                            sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(log.timestamp).toLocaleString()}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.username || log.user_email || 'N/A'}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.question_category || 'N/A'}</TableCell>
                            <TableCell sx={{ maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {truncateText(log.question, 90)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                              {log.is_question_a_follow_up_question == null ? 'N/A' : Number(log.is_question_a_follow_up_question) === 1 ? 'Yes' : 'No'}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              {log.answer_image && log.answer_image.startsWith('data:image/png;base64,') && log.answer_image !== EMPTY_IMAGE_BASE64 ? 'Yes' : 'No'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {/* Log Detail Dialog */}
                  <Dialog open={!!selectedLog} onClose={() => setSelectedLog(null)} maxWidth="md" fullWidth scroll="paper">
                    {selectedLog && (
                      <>
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Log Detail</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(selectedLog.timestamp).toLocaleString()} — {selectedLog.username || selectedLog.user_email || 'N/A'}
                            </Typography>
                          </Box>
                          <IconButton onClick={() => setSelectedLog(null)} size="small"><CloseIcon /></IconButton>
                        </DialogTitle>
                        <DialogContent dividers>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
                            <DetailRow label="Category">
                              <Typography variant="body2">{selectedLog.question_category || 'N/A'}</Typography>
                              {selectedLog.question_category_explanation && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {selectedLog.question_category_explanation}
                                </Typography>
                              )}
                            </DetailRow>
                            <DetailRow label="Follow-up?">
                              <Typography variant="body2">
                                {selectedLog.is_question_a_follow_up_question == null ? 'N/A' : Number(selectedLog.is_question_a_follow_up_question) === 1 ? 'Yes' : 'No'}
                              </Typography>
                            </DetailRow>
                            <DetailRow label="IP Address">
                              <Typography variant="body2">{selectedLog.user_ip || 'N/A'}</Typography>
                            </DetailRow>
                            <DetailRow label="Score Threshold">
                              <Typography variant="body2">{selectedLog.score_threshold.toFixed(2)}</Typography>
                            </DetailRow>
                            <DetailRow label="Only Transcripts?">
                              <Typography variant="body2">{selectedLog.use_only_transcripts_flag ? 'Yes' : 'No'}</Typography>
                            </DetailRow>
                            <DetailRow label="History Retention">
                              <Typography variant="body2">{selectedLog.history_retention}</Typography>
                            </DetailRow>
                          </Box>
                          <Divider sx={{ mb: 2 }} />
                          <DetailRow label="Question">
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: isDark ? 'background.default' : '#f9f9f9' }}>
                              <Typography variant="body2" component="div" dangerouslySetInnerHTML={{ __html: selectedLog.question }} />
                            </Paper>
                          </DetailRow>
                          <DetailRow label="Answer">
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: isDark ? 'background.default' : '#f9f9f9', maxHeight: 400, overflowY: 'auto' }}>
                              <Box
                              sx={{
                                '& p': { mt: 0, mb: '0.5em' },
                                '& h1, & h2, & h3, & h4': { mt: '1em', mb: '0.5em', fontWeight: 'bold', fontSize: '1.1rem' },
                                '& ul, & ol': { mt: 0, mb: '0.5em', pl: '1.5em' },
                                '& p:empty': { display: 'none' },
                                '& li:empty': { display: 'none' },
                                fontSize: '0.875rem',
                              }}
                            >
                              <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkBreaks]}>
                                {selectedLog.answer}
                              </ReactMarkdown>
                            </Box>
                            </Paper>
                          </DetailRow>
                          {selectedLog.answer_image && selectedLog.answer_image.startsWith('data:image/png;base64,') && selectedLog.answer_image !== EMPTY_IMAGE_BASE64 && (
                            <DetailRow label="Answer Image">
                              <Box component="img" src={selectedLog.answer_image} alt="Answer visual" sx={{ maxWidth: '100%', maxHeight: '400px', border: '1px solid', borderColor: 'divider', borderRadius: '8px' }} />
                            </DetailRow>
                          )}
                          <DetailRow label="User Agent">
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                              {selectedLog.user_agent || 'N/A'}
                            </Typography>
                          </DetailRow>
                        </DialogContent>
                      </>
                    )}
                  </Dialog>
                  </>
                ) : (
                  <Typography sx={{ mt: 2, textAlign: 'center' }}>No logs available for the selected criteria.</Typography>
                )
              )}
            </Paper>
          );
        })()}

        {/* Tab 2: AWS Cost Overview */}
        {activeTab === 2 && (
          <Paper elevation={3} sx={{ p: 2, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexShrink: 0 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                    AWS Cost Overview
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
              <ToggleButtonGroup
                color="primary"
                value={selectedAggregation}
                exclusive
                onChange={handleAggregationChange}
                aria-label="Aggregation Period"
              >
                <ToggleButton value="month" aria-label="monthly">Monthly</ToggleButton>
                <ToggleButton value="day" aria-label="daily">Daily</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                color="primary"
                value={costViewMode}
                exclusive
                onChange={(e, newMode) => { if (newMode) setCostViewMode(newMode as 'cost' | 'avg_cost_per_request'); }}
                aria-label="Cost View Mode"
              >
                <ToggleButton value="cost">Total Cost</ToggleButton>
                <ToggleButton value="avg_cost_per_request">Avg Cost/Req</ToggleButton>
              </ToggleButtonGroup>
              {selectedAggregation === 'day' && (
                <ToggleButtonGroup
                  color="primary"
                  value={logScaleRequestsScatter ? 'log' : 'linear'}
                  exclusive
                  onChange={(e, newScale) => {
                    if (newScale !== null) {
                      setLogScaleRequestsScatter(newScale === 'log');
                    }
                  }}
                  aria-label="Requests X-axis Scale"
                ><ToggleButton value="linear">Linear X-axis</ToggleButton><ToggleButton value="log">Log X-axis</ToggleButton></ToggleButtonGroup>
              )}
              <FormControl sx={{ m: 1, minWidth: 240, maxWidth: 300 }} size="small">
                <InputLabel id="service-multiple-checkbox-label">Services</InputLabel>
                <Select
                    labelId="service-multiple-checkbox-label"
                    id="service-multiple-checkbox"
                    multiple
                    value={selectedServices}
                    onChange={(event) => {
                        const {
                            target: { value },
                        } = event;
                        setSelectedServices(
                            typeof value === 'string' ? value.split(',') : value,
                        );
                    }}
                    label="Services"
                    renderValue={(selected) => (selected.length === serviceColumns.length) ? `All Services (${serviceColumns.length})` : `${selected.length} of ${serviceColumns.length} selected`}
                >
                    {serviceColumns.map((service) => (
                        <MenuItem key={service} value={service}>
                            <Checkbox checked={selectedServices.indexOf(service) > -1} />
                            <ListItemText primary={service} />
                        </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
            {isLoadingCosts && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}
            {costFetchError && <Typography color="error">{costFetchError.message}</Typography>}
            {!isLoadingCosts && !costFetchError && (
              (awsCosts.length > 0 && serviceColumns.length > 0) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                  <Grid container spacing={2} sx={{ mb: 4, flexShrink: 0 }}>
                    <Grid item xs={12} lg={selectedAggregation === 'day' ? 6 : 12}>
                      <Paper sx={{ height: '450px', width: '100%', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: '4px' }}>
                        <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>Cost Breakdown by Service</Typography>
                        {awsChartData.datasets.length > 0 && <Bar key={`cost-${isDark}`} options={getChartOptions(selectedAggregation)} data={awsChartData as ChartData<'bar'>} />}
                      </Paper>
                    </Grid>
                    {selectedAggregation === 'day' && (
                      <Grid item xs={12} lg={6}>
                        <Paper sx={{ height: '450px', width: '100%', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: '4px' }}>
                          {awsScatterChartData.datasets.length > 0 ? (
                            <Scatter key={`scatter-${isDark}`} options={getScatterChartOptions()} data={awsScatterChartData} />
                          ) : (
                            <Box sx={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center'}}><Typography>No data for scatter plot.</Typography></Box>
                          )}
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                  <TableContainer component={Paper} sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                    <Table stickyHeader aria-label="aws costs table" size="small" sx={{ tableLayout: 'auto' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', whiteSpace: 'nowrap', px: 1 }}>Date</TableCell>
                          {serviceColumns.filter(serviceName => selectedServices.includes(serviceName)).map(serviceName => (
                            <MuiTooltip key={serviceName} title={serviceName} arrow>
                              <TableCell
                                align="center"
                                sx={{
                                  fontWeight: 'bold',
                                  maxWidth: '80px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  px: 0.5,
                                  fontSize: '0.75rem',
                                }}
                              >
                                {serviceName}
                              </TableCell>
                            </MuiTooltip>
                          ))}
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 'bold', borderLeft: '2px solid', borderLeftColor: 'divider', whiteSpace: 'nowrap', px: 1, fontSize: '0.75rem' }}
                          >
                            Total
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: 1, fontSize: '0.75rem' }}>
                            Reqs
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: 1, fontSize: '0.75rem' }}>
                            Avg/Req
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {awsCosts.map((row) => (
                          <TableRow
                            key={row.date}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell component="th" scope="row" sx={{ textAlign: 'center', whiteSpace: 'nowrap', px: 1 }}>
                              {row.date}
                            </TableCell>
                            {serviceColumns.filter(serviceName => selectedServices.includes(serviceName)).map(serviceName => {
                              const value = typeof row[serviceName] === 'number' ? row[serviceName] as number : 0;
                              const cellStyle = getHeatmapCellStyle(value);
                              return (
                              <TableCell
                                key={`${row.date}-${serviceName}`}
                                align="center"
                                sx={{
                                  px: 0.5,
                                  fontSize: '0.75rem',
                                  ...cellStyle,
                                }}
                              >
                                {costViewMode === 'cost'
                                  ? value.toFixed(2)
                                  : value.toFixed(3)
                                }
                              </TableCell>
                            );
                            })}
                            <TableCell align="center" sx={{ fontWeight: 'bold', borderLeft: '2px solid', borderLeftColor: 'divider', whiteSpace: 'nowrap', px: 1, fontSize: '0.75rem' }}>
                              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(row.total_cost)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: 1, fontSize: '0.75rem' }}>
                              {row.total_requests.toLocaleString()}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: 1, fontSize: '0.75rem' }}>
                              {row.total_requests > 0
                                ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(row.total_cost / row.total_requests)
                                : 'N/A'
                              }
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Typography sx={{ mt: 2, textAlign: 'center' }}>No AWS cost data available for the selected period ({selectedAggregation}).</Typography>
              )
            )}
          </Paper>
        )}
      </Box>
    </Stack>
  );
}

export default Admin;
